// src/components/EditUserDialog.js
import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  MenuItem,
  Alert,
  CircularProgress,
  Box,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { updateUser } from '../services/userService';

const zones = [
  'ZONE A - LAGOS',
  'ZONE B - KADUNA',
  'ZONE B2 - KADUNA',
  'ZONE C - BAUCHI',
  'ZONE D - MINNA',
  'ZONE E - OWERRI',
  'ZONE F - IBADAN',
  'ZONE G - BENIN',
  'ZONE H - BENUE',
  'SERVICE HEADQUARTERS',
];

const EditUserDialog = ({ open, user, onClose, onSuccess, onNotify }) => {
  const [formData, setFormData] = useState({
    serviceNumber: '',
    firstName:     '',
    lastName:      '',
    role:          '',
    assignedZone:  '',
    isActive:      true,
  });
  const [error, setError]     = useState('');
  const [loading, setLoading] = useState(false);

  // FIX: Capture user.id into a ref when the dialog opens.
  // If the parent clears the `user` prop while the dialog is still open
  // (e.g. during a concurrent state update), the submit still has the correct ID.
  const userIdRef = useRef(null);
  const userEmailRef = useRef('');
  const userHasServiceNumber = useRef(false);

  useEffect(() => {
    if (open && user) {
      // Capture stable identifiers
      userIdRef.current = user.id;
      userEmailRef.current = user.email || '';
      userHasServiceNumber.current = Boolean(user.serviceNumber);

      // Pre-fill the form
      setFormData({
        serviceNumber: user.serviceNumber || '',
        firstName:     user.firstName || '',
        lastName:      user.lastName || '',
        role:          user.role || '',
        assignedZone:  user.assignedZone || '',
        isActive:      user.isActive ?? true,
      });
      setError('');
    }
  }, [open, user]);

  const handleChange = (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFormData({ ...formData, [e.target.name]: value });
    setError('');
  };

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setError('');

    // Validate service number format if provided
    if (formData.serviceNumber.trim()) {
      const snRegex = /^[A-Za-z0-9\/\-]+$/;
      if (!snRegex.test(formData.serviceNumber.trim())) {
        setError('Service number can only contain letters, numbers, slashes, and hyphens');
        return;
      }
    }

    // Validate zone for officers
    if (formData.role === 'VERIFICATION_OFFICER' && !formData.assignedZone) {
      setError('Please select a zone for verification officers');
      return;
    }

    // FIX: Use the captured ref ID — not user.id from prop (which may be null)
    const targetId = userIdRef.current;
    if (!targetId) {
      setError('Unable to identify user. Please close and reopen the dialog.');
      return;
    }

    setLoading(true);

    try {
      await updateUser(targetId, {
        serviceNumber: formData.serviceNumber.trim().toUpperCase() || undefined,
        firstName:     formData.firstName,
        lastName:      formData.lastName,
        role:          formData.role,
        assignedZone:  formData.assignedZone || null,
        isActive:      formData.isActive,
      });

      const fullName = `${formData.firstName} ${formData.lastName}`.trim();
      if (onNotify) onNotify(`User ${fullName} updated successfully`, 'success');
      onSuccess();
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to update user';
      setError(msg);
      if (onNotify) onNotify(msg, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setError('');
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit User</DialogTitle>
      <DialogContent>
        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* Email — read-only, shown from captured ref */}
          <TextField
            fullWidth
            label="Email"
            value={userEmailRef.current}
            disabled
            margin="normal"
            helperText="Email cannot be changed"
          />

          {/* Service Number */}
          <TextField
            fullWidth
            label="Service Number"
            name="serviceNumber"
            value={formData.serviceNumber}
            onChange={handleChange}
            margin="normal"
            disabled={loading}
            placeholder="e.g. NIS/2024/001"
            helperText={
              !userHasServiceNumber.current
                ? 'This user has no service number yet — please fill it in'
                : 'Update the service number if needed'
            }
            inputProps={{ style: { textTransform: 'uppercase' } }}
            sx={!userHasServiceNumber.current ? {
              '& .MuiOutlinedInput-root': { borderColor: 'warning.main' },
              '& .MuiFormHelperText-root': { color: 'warning.main' },
            } : {}}
          />

          <TextField
            fullWidth
            label="First Name"
            name="firstName"
            value={formData.firstName}
            onChange={handleChange}
            required
            margin="normal"
            disabled={loading}
          />

          <TextField
            fullWidth
            label="Last Name"
            name="lastName"
            value={formData.lastName}
            onChange={handleChange}
            required
            margin="normal"
            disabled={loading}
          />

          <TextField
            fullWidth
            select
            label="Role"
            name="role"
            value={formData.role}
            onChange={handleChange}
            required
            margin="normal"
            disabled={loading}
          >
            <MenuItem value="ADMIN">Admin</MenuItem>
            <MenuItem value="VERIFICATION_OFFICER">Verification Officer</MenuItem>
            <MenuItem value="MANAGEMENT">Management</MenuItem>
          </TextField>

          {/* Zone — only shown for Verification Officers */}
          {formData.role === 'VERIFICATION_OFFICER' && (
            <TextField
              fullWidth
              select
              label="Assigned Zone"
              name="assignedZone"
              value={formData.assignedZone}
              onChange={handleChange}
              required
              margin="normal"
              disabled={loading}
            >
              {zones.map((zone) => (
                <MenuItem key={zone} value={zone}>
                  {zone}
                </MenuItem>
              ))}
            </TextField>
          )}

          <FormControlLabel
            control={
              <Switch
                checked={formData.isActive}
                onChange={handleChange}
                name="isActive"
                disabled={loading}
              />
            }
            label="Account Active"
            sx={{ mt: 2 }}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={loading}>
          {loading ? <CircularProgress size={24} /> : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditUserDialog;